import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { Icon, NetworkLogoAvatar, PROVIDERS, api, naira, parsePlanValidity, payWithFlutterwaveInline, useAuth, validityBucket, discountedPrice } from "../lib/core";

export const ProviderCard = ({ provider, selected, onSelect }) => {
  const scale = provider.logoScale || 1;
  const logoSize = Math.round(40 * scale);
  
  return (
    <button
      data-testid={`provider-${provider.name.toLowerCase()}`}
      onClick={() => onSelect(provider.name)}
      className={`relative p-4 rounded-xl border-2 transition-all duration-200 hover:-translate-y-0.5 flex flex-col items-center gap-2 ${
        selected
          ? "border-primary-custom shadow-lg"
          : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
      style={{
        backgroundColor: selected ? provider.color : "white",
      }}
    >
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${
          selected ? "" : "bg-slate-50"
        }`}
        style={{ backgroundColor: selected ? "rgba(255,255,255,0.9)" : "" }}
      >
        <img 
          src={provider.logo} 
          alt={provider.name}
          className="object-contain"
          style={{ width: `${logoSize}px`, height: `${logoSize}px` }}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        <span 
          className="font-bold text-lg hidden items-center justify-center w-full h-full"
          style={{ color: provider.color }}
        >
          {provider.name.substring(0, 2)}
        </span>
      </div>
      <span
        className="font-semibold text-sm"
        style={{ color: selected ? provider.textColor : "#1e293b" }}
      >
        {provider.name}
      </span>
      {selected && (
        <div className="absolute top-2 right-2">
          <Icon name="check_circle" style={{ color: provider.textColor }} />
        </div>
      )}
    </button>
  );
};

// Service Type Button Component

export const ServiceTypeButton = ({ type, icon, label, selected, onClick }) => (
  <button
    data-testid={`service-${type}`}
    onClick={onClick}
    className={`flex-1 py-4 px-4 rounded-xl font-semibold transition-all duration-200 flex flex-col items-center gap-2 ${
      selected
        ? "bg-primary-custom text-white shadow-lg shadow-primary-custom/20"
        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
    }`}
  >
    <Icon name={icon} className="text-2xl" />
    <span>{label}</span>
  </button>
);

// Data Plan Card Component
// Extract validity (days count) from a plan description; returns null if unknown

export const DataPlanCard = ({ plan, selected, onSelect, discountPct = 0 }) => {
  // Primary allowance (split off bonuses)
  const parts = (plan.subCode || "").split("+");
  const main = parts[0]?.trim() || plan.subCode;
  const extras = parts.slice(1).map((s) => s.trim()).filter(Boolean);
  const days = parsePlanValidity(plan.description);
  const hasDiscount = discountPct > 0;
  const net = hasDiscount ? discountedPrice(plan.amount, discountPct) : plan.amount;

  return (
    <button
      type="button"
      data-testid={`data-plan-${plan.subCode}`}
      onClick={() => onSelect(plan)}
      className={`group relative p-3 text-left rounded-xl border transition-all duration-200 ${
        selected
          ? "border-primary-custom bg-primary-custom/5 shadow-md ring-2 ring-primary-custom/20"
          : "border-slate-200 bg-white hover:border-primary-custom/40 hover:shadow-sm"
      }`}
    >
      {selected && (
        <span className="absolute top-2 right-2 w-5 h-5 bg-primary-custom rounded-full flex items-center justify-center">
          <Icon name="check" className="text-white text-[14px]" />
        </span>
      )}

      {/* Main allowance (big) */}
      <div className="flex items-baseline gap-1.5 pr-5">
        <span className="font-bold text-slate-900 text-lg leading-tight truncate">{main}</span>
      </div>

      {/* Extras (e.g. "+10mins", "+7Sms") shown compact */}
      {extras.length > 0 && (
        <div className="mt-0.5 text-[11px] text-slate-500 truncate">+ {extras.join(" · ")}</div>
      )}

      {/* Price + validity chip */}
      <div className="mt-2 flex items-center justify-between gap-1">
        <span className="flex items-baseline gap-1.5 min-w-0">
          <span className="font-bold text-primary-custom text-base">₦{net.toLocaleString()}</span>
          {hasDiscount && (
            <span className="text-[11px] text-slate-400 line-through">₦{plan.amount.toLocaleString()}</span>
          )}
        </span>
        {days != null && (
          <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
            {days}d
          </span>
        )}
      </div>
      {hasDiscount && (
        <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid={`plan-discount-${plan.subCode}`}>
          <Icon name="sell" className="text-xs" /> {discountPct % 1 === 0 ? discountPct : discountPct.toFixed(1)}% off
        </span>
      )}
    </button>
  );
};

// Skeleton loader for data plans

export const DataPlanSkeleton = () => (
  <div className="grid grid-cols-2 gap-3">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="p-4 rounded-xl border-2 border-slate-100 bg-slate-50 animate-pulse">
        <div className="flex justify-between items-start">
          <div className="h-5 bg-slate-200 rounded w-16"></div>
          <div className="h-5 bg-slate-200 rounded w-20"></div>
        </div>
        <div className="h-3 bg-slate-200 rounded w-full mt-2"></div>
        <div className="h-3 bg-slate-200 rounded w-3/4 mt-1"></div>
      </div>
    ))}
  </div>
);

// Reusable data-plan picker: search + validity filters + price sort + scrollable card grid.
// Self-contained state; used by the Data Bundles tab AND the dashboard Quick Top-Up panel.

export const DataPlanSelector = ({ plans = [], loading = false, error = null, onRetry, selectedPlan, onSelect, maxHeight = "min(70vh, 640px)", cashbackRate = 0, discountPct = 0 }) => {
  const [planSearchQuery, setPlanSearchQuery] = useState("");
  const [planValidityFilter, setPlanValidityFilter] = useState("all"); // all | daily | weekly | monthly
  const [planSortBy, setPlanSortBy] = useState("price_asc"); // price_asc | price_desc

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden" data-testid="data-plan-selector">
      {/* Sticky header: title + count + search + filters + sort */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200/70 px-4 lg:px-5 pt-4 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Icon name="data_usage" className="text-primary-custom" /> Select Data Plan
            {cashbackRate > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full normal-case tracking-normal" data-testid="selector-cashback-tag">
                <Icon name="redeem" className="text-xs" /> {cashbackRate % 1 === 0 ? cashbackRate : cashbackRate.toFixed(1)}% back
              </span>
            )}
            {discountPct > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-primary-custom/10 text-primary-custom px-2 py-0.5 rounded-full normal-case tracking-normal" data-testid="selector-discount-tag">
                <Icon name="sell" className="text-xs" /> {discountPct % 1 === 0 ? discountPct : discountPct.toFixed(1)}% off
              </span>
            )}
          </h2>
          {plans.length > 0 && (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full" data-testid="plan-count">
              {plans.length} plans
            </span>
          )}
        </div>

        {plans.length > 4 && (
          <div className="relative">
            <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
            <input
              type="text"
              placeholder="Search plans (e.g., 5GB, 30 days, 1000)"
              value={planSearchQuery}
              onChange={(e) => setPlanSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-9 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 outline-none transition-all text-sm"
              data-testid="plan-search"
            />
            {planSearchQuery && (
              <button
                type="button"
                onClick={() => setPlanSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="plan-search-clear"
              >
                <Icon name="close" className="text-base" />
              </button>
            )}
          </div>
        )}

        {plans.length > 4 && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="plan-validity-filters">
              {[
                { id: "all", label: "All" },
                { id: "daily", label: "Daily" },
                { id: "weekly", label: "Weekly" },
                { id: "monthly", label: "Monthly" },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`plan-filter-${f.id}`}
                  onClick={() => setPlanValidityFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    planValidityFilter === f.id
                      ? "bg-primary-custom text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              data-testid="plan-sort-toggle"
              onClick={() => setPlanSortBy(planSortBy === "price_asc" ? "price_desc" : "price_asc")}
              className="ml-auto px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all flex items-center gap-1"
            >
              <Icon name="swap_vert" className="text-base" />
              <span>Price {planSortBy === "price_asc" ? "↑" : "↓"}</span>
            </button>
          </div>
        )}
      </div>

      {/* Scrollable plan grid */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-white to-transparent z-10" />
        <div
          className="overflow-y-auto custom-scrollbar px-4 lg:px-5 py-4"
          style={{ maxHeight }}
          data-testid="plan-scroll-container"
        >
          {loading ? (
            <DataPlanSkeleton />
          ) : error ? (
            <div className="text-center py-10">
              <Icon name="cloud_off" className="text-5xl text-slate-300" />
              <p className="mt-3 text-slate-700 font-medium">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-4 px-6 py-2.5 bg-primary-custom text-white rounded-lg hover:bg-primary-dark transition-all font-semibold"
                >
                  Retry
                </button>
              )}
            </div>
          ) : (() => {
            const q = planSearchQuery.trim().toLowerCase();
            const filtered = plans
              .filter((plan) => {
                if (planValidityFilter !== "all") {
                  if (validityBucket(parsePlanValidity(plan.description)) !== planValidityFilter) return false;
                }
                if (q) {
                  const hay = `${plan.subCode} ${plan.description} ${plan.amount}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              })
              .sort((a, b) => (planSortBy === "price_asc" ? a.amount - b.amount : b.amount - a.amount));

            if (filtered.length === 0) {
              return (
                <div className="text-center py-10" data-testid="plan-no-results">
                  <Icon name="search_off" className="text-5xl text-slate-300" />
                  <p className="mt-2 text-slate-500 text-sm">
                    {plans.length === 0 ? "No plans available for this network." : "No plans match your filters."}
                  </p>
                  {plans.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setPlanSearchQuery(""); setPlanValidityFilter("all"); }}
                      className="mt-3 text-primary-custom text-sm font-semibold hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {filtered.map((plan, i) => (
                  <DataPlanCard
                    key={`${plan.subCode}-${plan.amount}-${i}`}
                    plan={plan}
                    selected={selectedPlan?.subCode === plan.subCode && selectedPlan?.amount === plan.amount}
                    onSelect={onSelect}
                    discountPct={discountPct}
                  />
                ))}
              </div>
            );
          })()}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white to-transparent z-10" />
      </div>
    </div>
  );
};


// Transaction Item Component

export const TransactionItem = ({ transaction }) => {
  const provider = PROVIDERS.find((p) => p.name === transaction.provider);
  const isSuccess = transaction.status_code === "000" || transaction.status_code === "OK";
  const scale = provider?.logoScale || 1;
  const logoSize = Math.round(32 * scale);

  const downloadReceipt = async (e) => {
    e.stopPropagation();
    try {
      const { default: jsPDF } = await import("jspdf");
      const t = transaction;
      const doc = new jsPDF({ unit: "pt", format: "a5" });
      const W = doc.internal.pageSize.getWidth();
      doc.setFillColor(11, 18, 32); doc.rect(0, 0, W, 72, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
      doc.text("TopIt", 40, 44);
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text("Payment Receipt", W - 40, 44, { align: "right" });
      let y = 110;
      doc.setTextColor(17, 24, 39); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
      doc.text(`\u20a6${(t.amount || 0).toLocaleString()}`, 40, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120, 120, 120);
      y += 18; doc.text(`${(t.service_type || "").toUpperCase()} \u00b7 ${t.provider || ""}`, 40, y);
      y += 28;
      const rows = [
        ["Recipient", t.customer_phone || "\u2014"],
        ["Service", `${t.service_type || ""}${t.subcode ? ` (${t.subcode})` : ""}`],
        ["Amount", `\u20a6${(t.amount || 0).toLocaleString()}`],
        ["Paid via", t.source === "direct" ? "Card / Bank" : "Wallet"],
        ["Status", isSuccess ? "Success" : "Pending"],
        ["Date", t.timestamp ? new Date(t.timestamp).toLocaleString() : "\u2014"],
        ["Reference", t.transaction_reference || "\u2014"],
      ];
      doc.setFontSize(11);
      rows.forEach(([k, v]) => {
        doc.setTextColor(120, 120, 120); doc.text(String(k), 40, y);
        doc.setTextColor(17, 24, 39); doc.text(String(v), W - 40, y, { align: "right" });
        y += 22;
      });
      y += 8; doc.setDrawColor(230, 230, 230); doc.line(40, y, W - 40, y); y += 20;
      doc.setTextColor(150, 150, 150); doc.setFontSize(9);
      doc.text("Thank you for using TopIt.", 40, y);
      doc.save(`topit-receipt-${t.transaction_reference || "tx"}.pdf`);
    } catch (err) { /* ignore */ }
  };

  return (
    <div
      data-testid={`transaction-${transaction.transaction_reference}`}
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200"
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: provider?.color || "#e2e8f0",
        }}
      >
        {provider?.logo ? (
          <img 
            src={provider.logo} 
            alt={transaction.provider}
            className="object-contain bg-white rounded-full p-1"
            style={{ width: `${logoSize}px`, height: `${logoSize}px` }}
          />
        ) : (
          <span 
            className="font-bold text-sm"
            style={{ color: provider?.textColor || "#000" }}
          >
            {transaction.provider?.substring(0, 2)}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 truncate">{transaction.customer_phone}</p>
        <p className="text-xs text-slate-500 capitalize">
          {transaction.service_type}
          {transaction.subcode && ` • ${transaction.subcode}`}
        </p>
        {transaction.timestamp && (
          <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`tx-date-${transaction.transaction_reference}`}>
            {new Date(transaction.timestamp).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="font-bold text-slate-900">₦{transaction.amount?.toLocaleString()}</p>
        <div className={`flex items-center gap-1 text-xs ${isSuccess ? "text-green-600" : "text-amber-600"}`}>
          <Icon name={isSuccess ? "check_circle" : "schedule"} className="text-sm" />
          <span>{isSuccess ? "Success" : "Pending"}</span>
        </div>
      </div>
      <button onClick={downloadReceipt} data-testid={`tx-receipt-${transaction.transaction_reference}`} title="Download receipt"
        className="ml-1 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-slate-50 shrink-0">
        <Icon name="receipt_long" className="text-lg" />
      </button>
    </div>
  );
};

// Groups Page Component

export const RechargePaymentModal = ({ payment, serviceType = "airtime", onVerified, onClose }) => {
  const { user } = useAuth();
  const [verifying, setVerifying] = useState(false);
  const [hint, setHint] = useState("");
  const [phase, setPhase] = useState("ready"); // ready | paying | closed | verifying
  const amt = Number(payment.amount) || 0;
  const svc = serviceType === "data" ? "data" : "airtime";
  const phone = payment.customer_phone || user?.phone || "";

  const verify = async () => {
    if (!payment?.tx_ref) return;
    setVerifying(true); setPhase("verifying"); setHint("");
    try {
      const res = await api.get(`/payments/flutterwave/recharge/verify/${payment.tx_ref}`);
      if (res.data.status === "successful") {
        const vend = res.data.vend || { status_code: "PENDING", response_message: "Recharge is being processed" };
        onVerified(vend, res.data.recharge);
      } else if (res.data.status === "failed") {
        onVerified({ status_code: "error", response_message: "Payment failed. Please try again." });
      } else {
        setPhase("closed");
        setHint("Payment not completed yet. Finish the payment, then tap confirm.");
      }
    } catch (err) {
      setPhase("closed");
      setHint(err.response?.data?.detail || "Could not verify payment. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const launch = async () => {
    setHint(""); setPhase("paying");
    try {
      await payWithFlutterwaveInline({
        txRef: payment.tx_ref,
        amount: amt,
        customer: { email: user?.email, phone, name: user?.full_name },
        title: svc === "data" ? "TopIt Data Bundle" : "TopIt Airtime",
        description: `₦${amt.toLocaleString()} ${svc}${phone ? ` for ${phone}` : ""}`,
        onSuccess: () => { verify(); },
        onClose: () => { setPhase("closed"); },
      });
    } catch (e) {
      setPhase("ready");
      setHint(e.message || "Could not open payment. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="flw-recharge-modal" onClick={() => { if (phase !== "paying") onClose(); }}>
      <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Branded header */}
        <div className="bg-primary-custom px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/topit-logo-powered.png" alt="TopIt" className="h-7 object-contain brightness-0 invert" />
          </div>
          <button type="button" onClick={onClose} data-testid="flw-cancel-btn" className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center">
            <Icon name="close" className="text-lg" />
          </button>
        </div>

        <div className="p-6 text-center space-y-4">
          {/* Order summary */}
          <div className="flex items-center justify-center gap-3">
            <NetworkLogoAvatar provider={payment.provider} size={44} serviceType={serviceType} />
            <div className="text-left">
              <p className="text-2xl font-extrabold text-slate-900 leading-none">₦{amt.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">{payment.provider ? `${payment.provider} ` : ""}{svc}{phone ? ` • ${phone}` : ""}</p>
            </div>
          </div>

          {hint && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="flw-verify-hint">{hint}</p>}

          {phase === "ready" && (
            <>
              <button type="button" onClick={launch} data-testid="flw-pay-btn" className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2">
                <Icon name="lock" className="text-lg" /> Pay ₦{amt.toLocaleString()} securely
              </button>
              <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1"><Icon name="verified_user" className="text-sm" /> Secured by Flutterwave</p>
            </>
          )}

          {phase === "paying" && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-slate-600 text-sm">
                <Icon name="progress_activity" className="animate-spin text-primary-custom" /> Complete payment in the secure window…
              </div>
              <button type="button" onClick={verify} disabled={verifying} data-testid="flw-verify-btn" className="w-full h-11 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {verifying ? <><Icon name="progress_activity" className="animate-spin" /> Verifying…</> : <><Icon name="check_circle" /> I've completed payment</>}
              </button>
            </div>
          )}

          {(phase === "closed" || phase === "verifying") && (
            <div className="space-y-2">
              <button type="button" onClick={verify} disabled={verifying} data-testid="flw-verify-btn" className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {verifying ? <><Icon name="progress_activity" className="animate-spin" /> Verifying…</> : <><Icon name="check_circle" /> I've completed payment</>}
              </button>
              <button type="button" onClick={launch} data-testid="flw-pay-btn" className="w-full h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold flex items-center justify-center gap-2">
                <Icon name="refresh" className="text-lg" /> Pay again
              </button>
            </div>
          )}

          <button type="button" onClick={onClose} className="text-sm text-slate-400 hover:text-slate-600">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Success confirmation pop shown after a recharge is processed (inline flow or post-callback handoff).
export const RechargeConfirmationModal = ({ data, onClose }) => {
  if (!data) return null;
  const svcLabel = data.service_type === "data" ? "data" : "airtime";
  const when = data.processed_at
    ? new Date(data.processed_at).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
    : "";
  const delivered = data.delivered !== false;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="recharge-confirmation-modal" onClick={onClose}>
      {data.celebrate && delivered && (
        <Confetti width={typeof window !== "undefined" ? window.innerWidth : 1200} height={typeof window !== "undefined" ? window.innerHeight : 800} recycle={false} numberOfPieces={260} className="!z-[61]" />
      )}
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        className="bg-white rounded-3xl w-full max-w-sm p-7 text-center shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary-custom/10" />
        <div className="absolute -bottom-20 -left-16 w-44 h-44 rounded-full bg-emerald-500/10" />
        <div className="relative">
          <button onClick={onClose} data-testid="recharge-confirmation-close" className="absolute -top-1 -right-1 p-1.5 text-slate-400 hover:text-slate-700 rounded-lg">
            <Icon name="close" className="text-xl" />
          </button>
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ delay: 0.12, type: "spring", stiffness: 320, damping: 16 }}
            className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center ring-8 ring-emerald-50/60">
            <Icon name={delivered ? "check_circle" : "schedule"} className={`text-5xl ${delivered ? "text-emerald-500" : "text-amber-500"}`} />
          </motion.div>

          <div className="mt-4 flex items-center justify-center gap-3">
            <NetworkLogoAvatar provider={data.provider} size={44} serviceType={data.service_type} />
            <div className="text-left">
              <p className="text-2xl font-extrabold text-slate-900 leading-none">{naira(data.amount)}</p>
              <p className="text-xs text-slate-500 mt-1">{data.provider} {svcLabel}</p>
            </div>
          </div>

          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            Your <span className="font-bold text-slate-900">{data.provider}</span> {naira(data.amount)} {svcLabel} recharge for{" "}
            <span className="font-bold text-slate-900">{data.customer_phone}</span>{" "}
            {delivered ? "has been processed" : "is being processed"}
            {when ? <> on <span className="font-semibold text-slate-800">{when}</span></> : null}.
          </p>

          <button onClick={onClose} data-testid="recharge-confirmation-done" className="mt-6 w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold transition-all">
            Done
          </button>
          <button
            data-testid="recharge-confirmation-share"
            onClick={async () => {
              let inviteLine = `Top up airtime & data on TopIt 👉 ${window.location.origin}`;
              try {
                const { data: ref } = await api.get("/referral/info");
                if (ref?.referral_link) {
                  inviteLine = `I just topped up on TopIt ⚡ Get a bonus on your first recharge with my link 👉 ${ref.referral_link}`;
                }
              } catch { /* fall back to plain app link */ }
              const text = `✅ TopIt Receipt\n${data.provider} ${naira(data.amount)} ${svcLabel} for ${data.customer_phone}\n${delivered ? "Processed" : "Processing"}${when ? ` on ${when}` : ""}\n\n${inviteLine}`;
              try {
                if (navigator.share) { await navigator.share({ title: "TopIt Receipt", text }); }
                else { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank"); }
              } catch { /* user cancelled share */ }
            }}
            className="mt-2 w-full h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
            <Icon name="share" className="text-lg" /> Share receipt
          </button>
        </div>
      </motion.div>
    </div>
  );
};



export const CompactTxRow = ({ tx }) => {
  const isData = tx.service_type === "data";
  const ok = tx.status_code === "000" || tx.status_code === "OK";
  const pending = tx.status_code === "PENDING";
  const ts = tx.timestamp ? new Date(tx.timestamp) : null;
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`tx-row-${tx.id}`}>
      <NetworkLogoAvatar provider={tx.provider} size={40} serviceType={tx.service_type} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 text-sm truncate">
          {tx.provider ? `${tx.provider} ` : ""}{isData ? "Data" : "Airtime"}
        </p>
        <p className="text-xs text-slate-500 truncate">{tx.customer_phone}{tx.subcode ? ` · ${tx.subcode}` : ""}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-slate-900">₦{Number(tx.amount).toLocaleString()}</p>
        <p className="text-[11px] text-slate-400">{ts ? ts.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</p>
      </div>
      <span className={`hidden sm:inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${ok ? "bg-green-100 text-green-700" : pending ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-600"}`}>
        {ok ? "Successful" : pending ? "Pending" : "Failed"}
      </span>
    </div>
  );
};

// Desktop-only advert panel (right side on wide screens), managed from Admin → Content.
// Derive the advert slide list from /content (multi-advert array, with legacy single-advert fallback).
